import React from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useInventory } from '../context/InventoryContext';

const ProductCard = ({ product, variant = 'default' }) => {
  const { has, toggle } = useWishlist();
  const { getStock, getStatus } = useInventory();
  const saved = has(product.slug);
  const isFeatured = variant === 'featured';
  const isPlaceholder = !!product.placeholder;

  // Aggregate live status across variants (if any). Preference: any IN_STOCK
  // with stock > 0 → IN STOCK card. Else any PREORDER → PRE-ORDER card. Else OUT.
  let liveStatus = 'IN_STOCK';
  let liveStock = 0;
  if (!isPlaceholder) {
    const rows = product.variants && product.variants.length
      ? product.variants.map(v => ({ status: getStatus(product.slug, v.label), stock: getStock(product.slug, v.label) }))
      : [{ status: getStatus(product.slug, null), stock: getStock(product.slug, null) }];
    const anyInStock = rows.find(r => r.status === 'IN_STOCK' && r.stock > 0);
    const anyPreorder = rows.find(r => r.status === 'PREORDER');
    if (anyInStock) { liveStatus = 'IN_STOCK'; liveStock = anyInStock.stock; }
    else if (anyPreorder) { liveStatus = 'PREORDER'; }
    else { liveStatus = 'OUT_OF_STOCK'; }
  }

  const isPreorder = liveStatus === 'PREORDER';
  const isOutOfStock = liveStatus === 'OUT_OF_STOCK';

  const badge = () => {
    if (isPlaceholder) {
      return <span className="font-mono-label text-[10px] px-2 py-1 border border-[#ff2fa1]/40 text-[#ff2fa1] rounded">COMING SOON</span>;
    }
    if (isPreorder) {
      return <span className="font-mono-label text-[10px] px-2 py-1 border border-[#ff2fa1]/40 text-[#ff2fa1] rounded">PRE-ORDER</span>;
    }
    if (isOutOfStock) {
      return <span className="font-mono-label text-[10px] px-2 py-1 border border-white/30 text-neutral-400 rounded">OUT OF STOCK</span>;
    }
    // For variant products we don't show an exact count on the card (varies per variant).
    return product.variants && product.variants.length
      ? <span className="font-mono-label text-[10px] px-2 py-1 border border-emerald-500/30 text-emerald-400 rounded">IN STOCK</span>
      : <span className="font-mono-label text-[10px] px-2 py-1 border border-emerald-500/30 text-emerald-400 rounded">{liveStock} LEFT</span>;
  };

  return (
    <Link to={`/products/${product.slug}`} className="group product-card block bg-[#101013] border border-white/5 rounded-lg overflow-hidden">
      <div className="aspect-square overflow-hidden bg-black relative">
        <img src={product.image} alt={`${product.name} ${product.dose || ''} — research peptide from Vial Rat Research Co.`} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${isPlaceholder ? 'opacity-60' : ''}`} />
        {isPlaceholder && (
          <div className="absolute top-3 left-3 font-mono-label text-[10px] bg-[#ff2fa1] text-black px-2 py-1 rounded">COMING SOON</div>
        )}
        {isPreorder && !isPlaceholder && (
          <div className="absolute top-3 left-3 font-mono-label text-[10px] bg-[#ff2fa1] text-black px-2 py-1 rounded">COMING SOON · PRE-ORDER</div>
        )}
        <button
          type="button"
          aria-label={saved ? 'Remove from vault' : 'Save to vault'}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(product.slug); }}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full backdrop-blur bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors ${saved ? 'text-[#ff2fa1]' : 'text-white/80 hover:text-[#ff2fa1]'}`}>
          <Heart size={16} fill={saved ? '#ff2fa1' : 'transparent'} />
        </button>
      </div>
      <div className="p-5">
        {isFeatured && (
          <div className="font-mono-label text-[10px] text-[#ff2fa1] mb-2">{product.category}</div>
        )}
        {!isFeatured && (
          <div className="font-mono-label text-[10px] text-[#ff2fa1] mb-1">{product.dose}</div>
        )}
        <h3 className="font-serif-display text-white text-xl mb-1">{product.name}</h3>
        {product.subtitle && (
          <div className="font-mono-label text-[9px] text-[#ff2fa1] tracking-wider mb-1">{product.subtitle}</div>
        )}
        {isFeatured && (
          <div className="font-mono-label text-[10px] text-neutral-500 mb-3">{product.dose}</div>
        )}
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          <span className={`text-sm ${isPlaceholder ? 'text-neutral-500' : 'text-white'}`}>{product.priceDisplay}</span>
          {badge()}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
